import styled from '@emotion/styled/macro';
import { useMemo, useState } from 'react';
import {
  ScheduleInstallModal,
  SvgIcon,
  SvgNames,
  Typography,
  Switch,
  useSingletonMobileMediaQuery,
} from '@sunrun/experience-ui-components';
import {
  getSplatUserType,
  getSfdcEnv,
  UserProfile,
} from '@sunrun/sales-experience-shared';
import {
  Button,
  tokens,
  PipelineUrgentActionBanner,
  Skeleton,
} from '@sunrun/experience-ui-components';
import { usePipeline } from 'providers/Pipeline';
import { useHybridToken } from 'providers/Auth';
import { useInstallSchedule } from 'hooks/useInstallSchedule';
import { formatCurrency } from 'helpers/formatHelpers';
import { getCustomerProfileQuery } from 'helpers/listViewHelpers';
import {
  CHECKOUT,
  CITY_INSPECTION,
  CITY_PERMITTING,
  DESIGN_APPROVAL,
  HOME_REVIEW,
  INSTALL_PREP,
  INSTALLATION,
  POWER_ON,
  ARC,
  NEW_CUSTOMER,
  OFFER_CREATED,
} from 'components/atoms/PipelineConsts';
import { getUrls } from 'helpers/getUrlHelper';

interface UrgentTasksProps {
  userProfile: UserProfile;
}

const UrgentTasks = ({ userProfile }: UrgentTasksProps) => {
  const { hybridToken, hybridTokenType } = useHybridToken();
  const isMobile = useSingletonMobileMediaQuery();
  const [showCommission, setShowCommission] = useState(false);
  const splatUserType = userProfile ? getSplatUserType(userProfile) : undefined;

  const { data: pipelineData, isLoading: isPipelineLoading } = usePipeline(
    splatUserType,
    userProfile ? [userProfile.contactId] : undefined,
  );

  const {
    showInstallScheduleModalAndGetUrl,
    currentPipelineProspect,
    isLoadingSchedulerLink,
    fullSelfSchedulerUrl,
    schedulerURLError,
    serviceAppointmentId,
    closeModal,
    showScheduleInstallModal,
  } = useInstallSchedule({ hybridToken });

  const urgentTasks = useMemo(() => {
    if (!pipelineData?.prospects) {
      return [];
    }

    const allProspects = Object.entries(pipelineData.prospects)
      .flatMap(([, prospects]) => prospects || [])
      .filter((prospect) => prospect.urgentAction);

    // Get the correct stage order based on user type
    const getStageOrder = () => {
      if (!userProfile) return [];

      if (splatUserType === 'CX Pro') {
        return [
          ARC,
          CHECKOUT,
          HOME_REVIEW,
          DESIGN_APPROVAL,
          CITY_PERMITTING,
          INSTALL_PREP,
          INSTALLATION,
          CITY_INSPECTION,
          POWER_ON,
        ];
      }
      if (userProfile.isPartner) {
        return [
          NEW_CUSTOMER,
          OFFER_CREATED,
          CHECKOUT,
          INSTALLATION,
          CITY_INSPECTION,
          POWER_ON,
        ];
      }
      return [
        ARC,
        NEW_CUSTOMER,
        OFFER_CREATED,
        CHECKOUT,
        HOME_REVIEW,
        DESIGN_APPROVAL,
        CITY_PERMITTING,
        INSTALL_PREP,
        INSTALLATION,
        CITY_INSPECTION,
        POWER_ON,
      ];
    };

    const stageOrder = getStageOrder();

    // Sort in reverse order from pipeline view (oldest first, then by stage order)
    return allProspects.sort((a, b) => {
      // First, sort by urgentAction - put "OnHold" at the bottom
      const aIsOnHold = a.urgentAction === 'OnHold';
      const bIsOnHold = b.urgentAction === 'OnHold';

      if (aIsOnHold && !bIsOnHold) return 1; // a goes after b
      if (!aIsOnHold && bIsOnHold) return -1; // a goes before b

      // If both have same urgentAction status, sort by pipeline stage
      const aStageIndex = stageOrder.indexOf(a.pipelineStage);
      const bStageIndex = stageOrder.indexOf(b.pipelineStage);

      // Reverse the stage order (later stages first)
      return bStageIndex - aStageIndex;
    });
  }, [pipelineData, userProfile, splatUserType]);

  const urls = getUrls();

  return (
    <UrgentTasksContainer isMobile={isMobile}>
      <UrgentTasksHeader>
        <HeaderLeft>
          <HeaderTitle>My Tasks</HeaderTitle>
          <TaskCount>{isPipelineLoading ? '' : urgentTasks.length}</TaskCount>
        </HeaderLeft>
        <UrgentTasksHeaderButtonsContainer>
          <Switch
            checked={showCommission}
            testId="commission-switch"
            onClick={() => setShowCommission(!showCommission)}
            onChange={() => {}}
            size="large"
            icon={
              <SvgIcon
                name={SvgNames.DollarSign}
                color={tokens.BRAND_HERO_BLUE}
              />
            }
          />
          {!isMobile && (
            <ViewPipelineButton
              isMobile={false}
              size="sm"
              color="secondary"
              onClick={() => {
                window.parent.postMessage(
                  {
                    type: 'COMMAND:CHANGE_URL',
                    source: 'Dashboard',
                    payload: {
                      url: urls.oneBaseUrl + '/pipeline?filterUrgent=true',
                    },
                  },
                  '*',
                );
              }}>
              View in pipeline
            </ViewPipelineButton>
          )}
        </UrgentTasksHeaderButtonsContainer>
      </UrgentTasksHeader>

      <UrgentTasksList>
        {isPipelineLoading ? (
          <>
            <TaskSkeleton />
            <TaskSkeleton />
            <TaskSkeleton />
            <TaskSkeleton />
            <TaskSkeleton />
          </>
        ) : urgentTasks.length === 0 ? (
          <NoTasksMessage>
            <SvgIcon height={40} width={40} name={SvgNames.InboxEmpty} />
            <Typography variant="h3" fontWeight="bold">
              No tasks yet
            </Typography>
          </NoTasksMessage>
        ) : (
          urgentTasks.map((task, index) => (
            <StyledPipelineUrgentActionBanner
              key={`task.prospectId-${index}`}
              pipelineProspect={task}
              oneBaseUrl={urls.oneBaseUrl}
              salesforceBaseUrl={urls.salesforceBaseUrl}
              sfdcEnv={userProfile ? getSfdcEnv(userProfile) : 'SFDC'}
              isMobile={isMobile}
              sfPartnerBaseUrl={urls.sfPartnerBaseUrl}
              splatBaseUrl={urls.splatBaseUrl}
              token={hybridToken}
              tokenType={hybridTokenType}
              variant="row"
              customerName={task.firstName + ' ' + task.lastName}
              showInstallScheduleModalAndGetUrl={() => {
                showInstallScheduleModalAndGetUrl(task);
              }}
              commission={
                showCommission
                  ? formatCurrency(
                      userProfile.isFusion
                        ? task.fusionCommission
                        : task.commission,
                      0,
                      false,
                    )
                  : ''
              }
              viewCapNextSteps={() => {
                window.parent.postMessage(
                  {
                    type: 'COMMAND:CHANGE_URL',
                    source: 'Dashboard',
                    payload: {
                      url:
                        urls.oneBaseUrl +
                        '/customers/customer-profile/' +
                        task.prospectId +
                        getCustomerProfileQuery({ initialTab: 'status' }),
                    },
                  },
                  '*',
                );
              }}
              viewPendingTask={() => {
                window.parent.postMessage(
                  {
                    type: 'COMMAND:CHANGE_URL',
                    source: 'Dashboard',
                    payload: {
                      url:
                        urls.oneBaseUrl +
                        '/customers/customer-profile/' +
                        task.prospectId +
                        getCustomerProfileQuery({
                          initialTab: 'status',
                          openPendingTask: true,
                        }),
                    },
                  },
                  '*',
                );
              }}
            />
          ))
        )}
      </UrgentTasksList>
      {isMobile && (
        <ViewPipelineButton
          isMobile={isMobile}
          size="sm"
          color="secondary"
          onClick={() => {
            window.parent.postMessage(
              {
                type: 'COMMAND:CHANGE_URL',
                source: 'Dashboard',
                payload: {
                  url: urls.oneBaseUrl + '/pipeline?filterUrgent=true',
                },
              },
              '*',
            );
          }}>
          View in pipeline
        </ViewPipelineButton>
      )}

      {/* Install Schedule Modal */}
      {showScheduleInstallModal && currentPipelineProspect && (
        <ScheduleInstallModal
          pipelineProspect={currentPipelineProspect}
          oneBaseUrl={urls.oneBaseUrl}
          sfPartnerBaseUrl={urls.sfPartnerBaseUrl}
          salesforceBaseUrl={urls.salesforceBaseUrl}
          sfdcEnv={userProfile ? getSfdcEnv(userProfile) : 'SFDC'}
          onClose={closeModal}
          schedulerUrl={fullSelfSchedulerUrl}
          isLoadingSchedulerUrl={isLoadingSchedulerLink}
          schedulerURLError={schedulerURLError}
          serviceAppointmentId={serviceAppointmentId}
        />
      )}
    </UrgentTasksContainer>
  );
};

const StyledPipelineUrgentActionBanner = styled(PipelineUrgentActionBanner)<{
  isMobile: boolean;
}>`
  ${({ isMobile }) =>
    isMobile &&
    `
    padding: 8px 32px 8px 16px !important;
  `}
`;

const UrgentTasksContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f8f4eb;
  padding: 1.5rem;
  box-sizing: border-box;

  ${({ isMobile }) =>
    isMobile &&
    `
    padding: 16px;
  `}
`;

const UrgentTasksHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-shrink: 0;
`;

const UrgentTasksHeaderButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${tokens.BLACK};
`;

const TaskCount = styled.span`
  color: ${tokens.ERROR_20};
  font-weight: 600;
  font-size: 1.25rem;
`;

const ViewPipelineButton = styled(Button)<{ isMobile: boolean }>`
  font-size: 0.875rem;
  border: 1px solid ${tokens.BRAND_HERO_BLUE};
  background: transparent;
  padding: ${({ isMobile }) => (isMobile ? '4px 12px' : '2px 8px')} !important;
  color: ${tokens.BRAND_HERO_BLUE};

  ${({ isMobile }) =>
    isMobile &&
    `
    margin-top: 16px;
  `}
`;

const UrgentTasksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  overflow-y: auto;
`;

const NoTasksMessage = styled.div`
  text-align: center;
  padding: 2rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 240px;
`;

const TaskSkeleton = styled(Skeleton)`
  height: 72px;
  width: 100%;
`;

export { UrgentTasks };
